import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import BlogImage from "../../../images/blog-banner.png";

type BlogDetailHeaderData = {
  tagTitle: string;
  title: string;
  description: string;
  date: string;
  author: {
    image: {
      url: string;
    };
    name: string;
  };

  blogDetailMainImage: string;
  blogDetailVideoFile: string;

  socialMediaLinks: {
    linkIcon: {
      url: string;
      title: string;
    };
    linkAltText: string;
    linkUrl: string;
  }[];
};

type BlogHeaderDetailProps = {
  content: BlogDetailHeaderData;
};

const BlogHeaderDetail = ({ content }: BlogHeaderDetailProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [minHeight, setMinHeight] = useState(750);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    const updateHeight = () => {
      if (contentRef.current) {
        const contentHeight = contentRef.current.scrollHeight;
        const baseHeight = 750;
        const titleThreshold = 250;
        const extraContentHeight = Math.max(0, contentHeight - titleThreshold);
        setMinHeight(baseHeight + extraContentHeight);
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [content.title]);

  const togglePlayPause = () => {
    if (!showVideo) {
      setShowVideo(true);
      // Small timeout to ensure video element is rendered before playing
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play();
          setIsPlaying(true);
        }
      }, 10);
    } else if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <>
      <div className="blog-banner" style={{ position: "relative" }}>
        <div
          className="blog-banner-block"
          style={{ minHeight: `${minHeight}px` }}
        >
          <img src={BlogImage} alt="background" className="background-image" />

          <div className="blog-gradient-overlay"></div>
          <div className="container-fluid custom-spacer-col position-relative gap-5">
            <div
              ref={contentRef}
              className="blog-content-wrapper"
              style={{ marginTop: "170px" }}
            >
              <p className="heading-s" style={{ color: "#CDCDF3", letterSpacing: "0.3rem" }}>
                {content?.tagTitle}
              </p>
              <h1 className="blog-title blog-title-custom blog-heading-1">{content?.title}</h1>
              <div className="blog-detail-header-user">
                <div className="blog-detail-header-user-image">
                  <img
                    src={content?.author?.image?.url ?? ""}
                    alt="author"
                    className="blog-detail-header-user-image"
                    style={{ objectFit: "scale-down" }}
                  />
                </div>
                <div className="blog-detail-header-user-details">
                  <h4>{content?.author?.name}</h4>
                  <span className="blog-date" style={{ color: "#A8A8A8" }}>
                    {dayjs(content.date).format("MMM D, YYYY")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="blog-detail-image-wrapper">
          {content?.blogDetailVideoFile ? (
            <>
              {!showVideo ? (
                <>
                  <img
                    src={content?.blogDetailMainImage}
                    alt="blog detail"
                    className="blog-detail-image"
                  />
                  <button
                    onClick={togglePlayPause}
                    className="video-detail-play-pause-btn"
                  >
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M3 1L10 6L3 11V1Z" fill="black" />
                    </svg>
                  </button>
                </>
              ) : (
                <>
                  <video
                    ref={videoRef}
                    src={content.blogDetailVideoFile}
                    className="blog-detail-image"
                    style={{
                      objectFit: "contain",
                      objectPosition: "center",
                      backgroundColor: "black",
                    }}
                  />
                  <button
                    onClick={togglePlayPause}
                    className="video-detail-play-pause-btn"
                  >
                    {isPlaying ? (
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3 1V11"
                          stroke="black"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M9 1V11"
                          stroke="black"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M3 1L10 6L3 11V1Z" fill="black" />
                      </svg>
                    )}
                  </button>
                </>
              )}
            </>
          ) : (
            <img
              src={content?.blogDetailMainImage}
              alt="blog detail"
              className="blog-detail-image"
            />
          )}
        </div>
      </div>
    </>
  );
};

export default BlogHeaderDetail;